/**
 * The sign-in form, in the three shapes the backend can ask for:
 *
 * - `cognito` / `credentials`: email + password (SRP happens in the container);
 * - `cognito` / `new-password`: an admin-created user's first sign-in;
 * - `dev`: a user id only — a node with development auth (no password exists).
 *
 * The typed values are this component's own UI state; what they MEAN is the
 * container's (`onSubmit`, `onSubmitNewPassword`). `pending` disables the
 * form while a request is out; `error` is shown as an alert.
 */
import { type FormEvent, useState } from 'react';
import { Button } from './ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card.js';
import { Field, FieldDescription, FieldError } from './ui/field.js';
import { Input } from './ui/input.js';
import { Label } from './ui/label.js';

export type LoginFormProps = {
  readonly mode: 'cognito' | 'dev';
  readonly step: 'credentials' | 'new-password';
  /** Where the user is signing in to (the backend's label). */
  readonly target: string;
  readonly pending: boolean;
  readonly error: string | null;
  readonly onSubmit: (credentials: { readonly identifier: string; readonly password: string }) => void;
  readonly onSubmitNewPassword: (newPassword: string) => void;
};

type FieldState = { readonly value: string; readonly onChange: (value: string) => void; readonly disabled: boolean };

function NewPasswordField({ value, onChange, disabled }: FieldState) {
  return (
    <Field>
      <Label htmlFor="login-new-password">New password</Label>
      <Input id="login-new-password" type="password" autoComplete="new-password" value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} required />
    </Field>
  );
}

function IdentifierField({ mode, value, onChange, disabled }: FieldState & { readonly mode: LoginFormProps['mode'] }) {
  const dev = mode === 'dev';
  return (
    <Field>
      <Label htmlFor="login-identifier">{dev ? 'User id' : 'Email'}</Label>
      <Input id="login-identifier" type={dev ? 'text' : 'email'} autoComplete={dev ? 'username' : 'email'} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} required />
      {dev ? <FieldDescription>Development sign-in: this node mints a token for any user id.</FieldDescription> : null}
    </Field>
  );
}

function PasswordField({ value, onChange, disabled }: FieldState) {
  return (
    <Field>
      <Label htmlFor="login-password">Password</Label>
      <Input id="login-password" type="password" autoComplete="current-password" value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} required />
    </Field>
  );
}

function submitLabel(pending: boolean, newPasswordStep: boolean): string {
  if (pending) return 'Signing in…';
  return newPasswordStep ? 'Set password and sign in' : 'Sign in';
}

export function LoginForm({ mode, step, target, pending, error, onSubmit, onSubmitNewPassword }: LoginFormProps) {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const newPasswordStep = mode === 'cognito' && step === 'new-password';

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (newPasswordStep) onSubmitNewPassword(newPassword);
    else onSubmit({ identifier: identifier.trim(), password });
  };
  const empty = newPasswordStep ? newPassword === '' : identifier.trim() === '';

  return (
    <Card className="w-full max-w-sm" data-testid="login-form">
      <CardHeader>
        <CardTitle>{newPasswordStep ? 'Choose a new password' : 'Sign in to ACP'}</CardTitle>
        <CardDescription>{newPasswordStep ? 'Your account needs a new password before first use.' : target}</CardDescription>
      </CardHeader>
      <CardContent>
        <form className="grid gap-4" onSubmit={submit} noValidate>
          {newPasswordStep ? <NewPasswordField value={newPassword} onChange={setNewPassword} disabled={pending} /> : null}
          {newPasswordStep ? null : <IdentifierField mode={mode} value={identifier} onChange={setIdentifier} disabled={pending} />}
          {!newPasswordStep && mode === 'cognito' ? <PasswordField value={password} onChange={setPassword} disabled={pending} /> : null}
          <FieldError>{error}</FieldError>
          <Button type="submit" disabled={pending || empty}>
            {submitLabel(pending, newPasswordStep)}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
